
import { Component, OnInit, signal, WritableSignal } from '@angular/core';
import { HttpHeaders } from '@angular/common/http';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { SimulationsInfoComponent } from '../../common/dashboard/simulations-info/simulations-info.component';
import { RunningSimulation, SimulationActionEvent } from '../../core/models/simulation.model';
import { UserInfo } from '../../core/models/user-info.model';
import { SimulationsService } from '../../services/simulations/simulations.service';
import { UserManagementService } from '../../services/user-management.service';
import { LoadingService } from '../../services/loading.service';
import { SnackbarService } from '../../services/snackbar.service';
import { Destroyable } from '../../utils/mixins/destroyable.mixin';
import { interval, startWith, switchMap } from 'rxjs';
import { DEFAULT_DELAY_TIME } from '../../core/consts/consts';
import { COMPONENT_ID_HEADER } from '../../core/consts/api.const';

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [MatProgressSpinnerModule, SimulationsInfoComponent],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.scss',
})
export class DashboardComponent extends Destroyable(Object) implements OnInit {
  private readonly componentId = 'dashboard';
  private hasLoadedOnce = false;
  allSimulations: WritableSignal<RunningSimulation[]> = signal<RunningSimulation[]>([]);
  filteredSimulations: WritableSignal<RunningSimulation[]> = signal<RunningSimulation[]>([]);
  currentUser: UserInfo | undefined;
  isLoading = false;

  constructor(
    private simulationsService: SimulationsService,
    private userManagementService: UserManagementService,
    private loadingService: LoadingService,
    private snackbarService: SnackbarService,
  ) {
    super();
  }

  get currentUserEmail(): string {
    return this.currentUser?.email ?? '';
  }

  ngOnInit(): void {
    this.initCurrentUser();
    this.subscribeToLoadingState();
    this.startSimulationsPolling();
  }

  onSimulationAction(event: SimulationActionEvent): void {
    const actionText = event.action === 'Retry' ? 'Restarting' : 'Canceling';
    const successMessage = event.action === 'Retry'
      ? 'Simulation has been restarted'
      : 'Simulation has been canceled';

    this.snackbarService.openCustom({
      message: `${actionText} simulation...`,
      type: 'info'
    });

    this.simulationsService
      .runningSimulationsAction(event.id, event.action)
      .pipe(
        this.takeUntilDestroyed()
      )
      .subscribe((response) => {
        if (response !== null) {
          this.refreshSimulations();
          this.snackbarService.openCustom({
            message: successMessage,
            type: 'success'
          });
        }
      });
  }

  private refreshSimulations(): void {
    const options = {
      headers: new HttpHeaders({
        [COMPONENT_ID_HEADER]: this.componentId,
      }),
    };

    this.simulationsService
      .getRunningSimulations(options)
      .pipe(
        this.takeUntilDestroyed(),
      )
      .subscribe((simulations: RunningSimulation[]) => {
        this.updateSimulations(simulations);
      });
  }

  private startSimulationsPolling(): void {
    const options = {
      headers: new HttpHeaders({
        [COMPONENT_ID_HEADER]: this.componentId,
      }),
    };

    this.loadingService.startLoading(this.componentId);

    interval(DEFAULT_DELAY_TIME)
      .pipe(
        startWith(0),
        this.takeUntilDestroyed(),
        switchMap(() => {
          const requestOptions = !this.hasLoadedOnce ? options : {};
          return this.simulationsService.getRunningSimulations(requestOptions)
        }),
      )
      .subscribe((simulations: RunningSimulation[]) => {
        this.updateSimulations(simulations);
        this.hasLoadedOnce = true;
      });
  }

  private updateSimulations(simulations: RunningSimulation[]): void {
    const hasChanged = JSON.stringify(this.allSimulations()) !== JSON.stringify(simulations);

    if (hasChanged) {
      const filteredSimulations = simulations.filter(
        (simulation: RunningSimulation) => simulation.userEmail === this.currentUserEmail,
      );

      this.allSimulations.set(simulations);
      this.filteredSimulations.set(filteredSimulations);
    }
  }

  private subscribeToLoadingState(): void {
    this.loadingService
      .getLoadingState(this.componentId)
      .pipe(this.takeUntilDestroyed())
      .subscribe((loadingState: boolean) => {
        this.isLoading = loadingState;
      });
  }

  private initCurrentUser(): void {
    this.currentUser = this.userManagementService.getUserInfo();
  }
}
